import { agreementAiConsentOf, agreementSignedFor, type V2Agreement } from './agreement';
import { assertedPropertyState, draftContextOrigin, MAX_SUPPORTED_DWELLING_UNITS, type InspectionContextOrigin, type V2Property } from './inspection';
import { inspectionServiceOf } from './inspectionService';
import { jurisdictionSupport, type JurisdictionCode, type JurisdictionStatus, type JurisdictionSupport } from './jurisdiction';

// The NY F1 policy gates, as pure derivations (Round 1, amended by the Owner rulings of 2026-09-09).
//
// Authority: NY-SCOPE-AUTHORITY-2026-09-06-F1 §15.1 / §15.2 through the frozen implementation plan
// §15 and Owner correction OC-1, as amended by NY-OWNER-RULINGS-RATIFICATION-20260909.md.
//
//   PP-20  AMENDED BY OD-9. A property that was never previously occupied is no longer blocked: it
//          is served as a New Build Visual Observation, with its own agreement and report
//          (src/domain/inspectionService.ts). What IS blocked here is the statutory unit limit
//          (CK-10 / LR-06): New York defines a home inspection for one to four dwelling units.
//   PP-21  AMENDED BY OD-8. Cloud AI is allowed for a real New York client when the agreement
//          signed for this engagement carries the consent clause AND the client consented;
//          otherwise it is blocked with the precise reason. Demo and internal-test contexts keep
//          today's working AI path.
//   X-1    Owner default 2026-09-11: a formal inspection may be CREATED before the agreement is
//          signed (an appointment), but field work does not start until both parties signed it.
//
// Everything here is pure, synchronous and offline. Nothing is inferred from year built, address,
// MLS-like data, photographs, external appearance, checklist content or AI.

// What an inspection IS, for policy purposes. Derived; never typed in by anyone.
export type InspectionContextKind = 'realClient' | 'nonClientTest';

// Two independent facts must BOTH say "not a client" before an inspection leaves the real-client
// side, so neither direction of relabelling opens the blocked capability:
//   - the frozen creation-time origin (a real client engagement can never become a Demo), and
//   - the live Demo flag (a Demo that is turned into a paid, signed engagement stops being one).
// Anything unknown — including every record written before the field existed — is a real client.
export interface ContextSubject {
  contextOrigin: InspectionContextOrigin | null;
  agreement: { demo: boolean };
}

export function contextKindOf(inspection: ContextSubject): InspectionContextKind {
  switch (inspection.contextOrigin) {
    case 'internalTest':
      return 'nonClientTest';
    case 'demo':
      return inspection.agreement.demo ? 'nonClientTest' : 'realClient';
    default:
      return 'realClient';
  }
}

// What the creation form gates on before any record exists. Built from the same derivation
// createInspection() will use, so no screen ever states an inspection's context itself.
export function draftContextSubject(agreement: { demo: boolean }): ContextSubject {
  return { contextOrigin: draftContextOrigin(agreement), agreement };
}

export type LegalReviewBlockerId = 'UNIT_COUNT_OVER_LIMIT';

export interface LegalReviewBlock {
  blockerId: LegalReviewBlockerId;
  // The open legal-review queue items this block stands on. Recorded for the audit trail; these
  // identifiers are never shown to an inspector or a client.
  lrIds: readonly string[];
}

const UNIT_COUNT_BLOCK: LegalReviewBlock = Object.freeze({
  blockerId: 'UNIT_COUNT_OVER_LIMIT',
  lrIds: Object.freeze(['LR-06'])
});

// CK-10 (Owner ruling OD-6, 2026-09-09). Only a recorded count ABOVE the statutory limit blocks.
// UNKNOWN (null) is NOT a block — it is an unanswered question, because a stored record must never
// be voided or reinterpreted by a schema change. (Until 2026-09-09 this function carried PP-20's
// new-construction block; OD-9 replaced that block with the New Build service.)
export function legalReviewBlockOf(inspection: { property: Pick<V2Property, 'unitCount'> }): LegalReviewBlock | null {
  const units = inspection.property.unitCount;
  return typeof units === 'number' && units > MAX_SUPPORTED_DWELLING_UNITS ? UNIT_COUNT_BLOCK : null;
}

// Whether the FORMAL inspection workflow may continue for this inspection right now.
//
// OC-1 (Owner, frozen 2026-09-06): a legacy record may always be opened and viewed and its data is
// preserved untouched — but the formal New York workflow may not CONTINUE while a required fact is
// unanswered. Checking PP-20 only at report generation is forbidden: it discovers a possibly
// newly-constructed property after the whole field pass.
export type FormalWorkflowGate =
  // The supported New York workflow may continue (home inspection or New Build observation).
  | { kind: 'clear'; authorityVersion: string }
  // Not a client engagement (Demo / internal test): the New York client gates do not apply.
  | { kind: 'nonClientTest' }
  | { kind: 'inspectorStateMissing' }
  | { kind: 'propertyStateUnconfirmed' }
  | { kind: 'unsupportedPropertyState'; state: JurisdictionCode; status: JurisdictionStatus }
  | { kind: 'stateMismatch'; inspectorState: JurisdictionCode; propertyState: JurisdictionCode }
  // The occupancy question decides WHICH service applies (OD-9) and is answered once.
  | { kind: 'occupancyUnconfirmed' }
  // CK-10: more dwelling units than the statutory definition covers.
  | { kind: 'legalReviewBlock'; block: LegalReviewBlock }
  // X-1: eligible, but the pre-inspection agreement for this service is not signed yet. Creation
  // and viewing are unaffected — only field work waits.
  | { kind: 'agreementUnsigned' };

export interface FormalWorkflowInput {
  inspection: ContextSubject & { property: Pick<V2Property, 'state' | 'stateConfirmed' | 'previouslyOccupied'> & { unitCount?: number | null } };
  // The licensed state from the device inspector profile; null = no profile / not entered.
  inspectorState: JurisdictionCode | null;
  // X-1: whether the agreement for THIS engagement is signed. Omitted = the question is not asked —
  // the creation form (an appointment may be created unsigned) and the signing path itself, which
  // would otherwise be refused for the very state it is there to leave.
  agreementSigned?: boolean;
}

export function formalWorkflowGate(input: FormalWorkflowInput): FormalWorkflowGate {
  if (contextKindOf(input.inspection) === 'nonClientTest') return { kind: 'nonClientTest' };
  const support: JurisdictionSupport = jurisdictionSupport({
    inspectorState: input.inspectorState,
    propertyState: assertedPropertyState(input.inspection.property)
  });
  if (support.kind !== 'supported') return support;
  const block = legalReviewBlockOf({ property: { unitCount: input.inspection.property.unitCount ?? null } });
  if (block) return { kind: 'legalReviewBlock', block };
  if (input.inspection.property.previouslyOccupied === null) return { kind: 'occupancyUnconfirmed' };
  if (input.agreementSigned === false) return { kind: 'agreementUnsigned' };
  return { kind: 'clear', authorityVersion: support.authorityVersion };
}

// Whether the agreement on this record is the signed agreement for the service the property calls
// for (X-1 / OD-9). The one place the two facts are paired, so no screen re-states the rule.
export function agreementSignedForInspection(inspection: { property: Pick<V2Property, 'previouslyOccupied'>; agreement: V2Agreement }): boolean {
  return agreementSignedFor(inspection.agreement, inspectionServiceOf(inspection.property));
}

// True when the formal workflow must not continue. `nonClientTest` is not blocked: a Demo or an
// internal-test inspection is not a New York client engagement and is not gated by these rules.
export function formalWorkflowBlocked(gate: FormalWorkflowGate): boolean {
  return gate.kind !== 'clear' && gate.kind !== 'nonClientTest';
}

// PP-21 as amended by OD-8. Why a cloud AI request must not be made for this inspection, or null
// when it may proceed.
//
// There are exactly TWO `null` returns in this function: the non-client-test context, and a real
// client engagement whose SIGNED agreement carries the consent clause and whose client CONSENTED.
// Every other real client engagement returns a block, whatever else is true of it — including one
// that cannot be classified at all — so a future code path that forgets to classify itself is
// blocked, not allowed.
//
//   blockedRealClient        the inspection could not be resolved at all (the chokepoint's default)
//   blockedJurisdiction      licence / property state / occupancy not settled
//   blockedLegalReview       the statutory unit limit (CK-10)
//   blockedAgreementUnsigned no signed agreement for this engagement yet (X-1)
//   blockedAgreementOutdated signed under a copy that carries no consent clause
//   blockedNotConsented      signed under the current copy; consent not given, or withdrawn
export type AiPolicyBlock = 'blockedRealClient' | 'blockedLegalReview' | 'blockedJurisdiction' | 'blockedAgreementUnsigned' | 'blockedAgreementOutdated' | 'blockedNotConsented';

export interface AiPolicyInput {
  inspection: FormalWorkflowInput['inspection'] & { agreement: V2Agreement };
  inspectorState: JurisdictionCode | null;
}

export function aiPolicyBlockOf(input: AiPolicyInput): AiPolicyBlock | null {
  if (contextKindOf(input.inspection) === 'nonClientTest') return null;
  const gate = formalWorkflowGate({ ...input, agreementSigned: agreementSignedForInspection(input.inspection) });
  if (gate.kind === 'legalReviewBlock') return 'blockedLegalReview';
  if (gate.kind === 'agreementUnsigned') return 'blockedAgreementUnsigned';
  if (gate.kind !== 'clear') return 'blockedJurisdiction';
  // Supported New York, real client, signed for this service — now the client's own decision
  // decides (agreement v2 §1; 19 NYCRR 197-4.3 prior consent / RPL §444-g(3) express consent).
  const consent = agreementAiConsentOf(input.inspection.agreement);
  if (consent === 'versionOutdated') return 'blockedAgreementOutdated';
  if (consent !== 'consented') return 'blockedNotConsented';
  return null;
}

// The audit marker the frozen plan requires on every non-client AI run record (§15.2 "marked
// NON_CLIENT_TEST_CONTEXT on every run record"; §17 "mark every non-client run
// NON_CLIENT_TEST_CONTEXT"; §19.2 Round 1). It is what tells a later reader — an auditor, counsel,
// the Owner — that a run was a Demo or an internal test rather than a New York client engagement,
// which is the distinction LR-02 / LR-19 / LR-20 turn on.
//
// It is DERIVED from the same classification that decides whether the call may happen at all. It is
// never an input, a toggle or a caller's claim, so a real client engagement cannot obtain it: the
// only way to be marked is to already be `nonClientTest`, which requires the creation-time origin.
// A record written before this field existed reads back unmarked, never guessed.
export const NON_CLIENT_TEST_CONTEXT = 'NON_CLIENT_TEST_CONTEXT';

export type NonClientTestContextMarker = typeof NON_CLIENT_TEST_CONTEXT;

export function nonClientTestMarkerOf(inspection: ContextSubject): NonClientTestContextMarker | null {
  return contextKindOf(inspection) === 'nonClientTest' ? NON_CLIENT_TEST_CONTEXT : null;
}

export function isNonClientTestMarker(value: unknown): value is NonClientTestContextMarker {
  return value === NON_CLIENT_TEST_CONTEXT;
}
